import React, { useCallback, useMemo } from "react";
import {
  HiCalendarDays,
  HiLanguage,
  HiSquares2X2,
  HiStar,
  HiUser,
} from "react-icons/hi2";

import Filter from "./Filter";
import { useQuestionMeta } from "../../context/assessSections/QuestionMetaContext";
import { useQuestionLibrary } from "../../context/assessSections/QuestionLibraryContext";
import { buildQuestionFilterQuery } from "./helpers/questionLibraryHelpers";

const questionLibraryFilterConfig = [
  { key: "grade", label: "Grade", icon: HiStar, optionKey: "gradeOptions" },
  { key: "language", label: "Language", icon: HiLanguage, optionKey: "languageOptions" },
  { key: "subject", label: "Subject", icon: HiSquares2X2, optionKey: "subjectOptions" },
  { key: "term", label: "Term", icon: HiSquares2X2, optionKey: "termOptions" },
  { key: "author", label: "Author", icon: HiSquares2X2, optionKey: "authorOptions" },
  { key: "chapter", label: "Chapter", icon: HiSquares2X2, optionKey: "chapterOptions" },
  { key: "difficulty", label: "Difficulty", icon: HiSquares2X2, optionKey: "difficultyOptions" },
  { key: "category", label: "Category", icon: HiSquares2X2, optionKey: "categoryOptions" },
  { key: "type", label: "Type", icon: HiSquares2X2, optionKey: "typeOptions" },
  { key: "source", label: "Source", icon: HiSquares2X2, optionKey: "sourceOptions" },
  { key: "createdBy", label: "Created By", icon: HiUser, optionKey: "createdByOptions" },
  { key: "year", label: "Year", icon: HiCalendarDays, optionKey: "yearOptions" },
];

const toSelectOptions = (list, fieldConfig = {}) => {
  const idKeys = fieldConfig.idKeys || [];
  const labelKeys = fieldConfig.labelKeys || [];

  const values = (list || [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        const value = String(item);
        return { value, label: value };
      }

      if (!item || typeof item !== "object") return null;

      const idKey = idKeys.find(
        (candidateKey) =>
          item[candidateKey] !== undefined && item[candidateKey] !== null
      );

      const labelKey = labelKeys.find(
        (candidateKey) =>
          item[candidateKey] !== undefined && item[candidateKey] !== null
      );

      if (!idKey || !labelKey) return null;

      const label =
        labelKey === "first_name"
          ? [item.first_name, item.last_name].filter(Boolean).join(" ") ||
            item.user_code
          : item[labelKey];

      return {
        value: String(item[idKey]),
        label: String(label),
      };
    })
    .filter(Boolean);

  const uniqueByValue = [
    ...new Map(values.map((option) => [option.value, option])).values(),
  ];

  return uniqueByValue.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};

export default function QuestionLibraryFilter({
  count = 0,
  countLabel = "Number of Questions",
  searchAriaLabel = "Search questions",
  panelFirst = true,
}) {
  // const { meta } = useQuestionMeta();
  const { getMetaByScope } = useQuestionMeta();

  const meta = getMetaByScope({ scope: "library" });

  const {
    query,
    applyQuery,
    // activeTab,
    // createdByOptions = [],
  } = useQuestionLibrary();

  const metaDataOptions = useMemo(() => {
    return {
      gradeOptions: toSelectOptions(meta.grades, {
        idKeys: ["grade_id", "id"],
        labelKeys: ["grade", "short_code"],
      }),
      languageOptions: toSelectOptions(meta.languages, {
        idKeys: ["language_id", "id"],
        labelKeys: ["language", "short_code"],
      }),
      subjectOptions: toSelectOptions(meta.subjects, {
        idKeys: ["subject_id", "id"],
        labelKeys: ["subject", "short_code"],
      }),
      termOptions: toSelectOptions(meta.terms, {
        idKeys: ["term_id", "id"],
        labelKeys: ["term", "short_code"],
      }),
      authorOptions: toSelectOptions(meta.authors, {
        idKeys: ["author_id", "id"],
        labelKeys: ["author", "ref_id"],
      }),
      chapterOptions: toSelectOptions(meta.chapters, {
        idKeys: ["chapter_id", "id"],
        labelKeys: ["chapter"],
      }),
      difficultyOptions: toSelectOptions(meta.difficulties, {
        idKeys: ["difficulty_id", "id"],
        labelKeys: ["difficulty_type"],
      }),
      categoryOptions: toSelectOptions(meta.categories, {
        idKeys: ["category_id", "id"],
        labelKeys: ["name"],
      }),
      typeOptions: toSelectOptions(meta.types, {
        idKeys: ["type_id", "id"],
        labelKeys: ["qtype"],
      }),
      sourceOptions: toSelectOptions(meta.sources, {
        idKeys: ["source_id", "id"],
        labelKeys: ["source"],
      }),
      createdByOptions: toSelectOptions(meta.creators, {
        idKeys: ["user_id"],
        labelKeys: ["label", "full_name", "first_name", "user_code", "created_by"],
      }),
      yearOptions: toSelectOptions(meta.years, {
        idKeys: ["year_id", "id"],
        labelKeys: ["year"],
      }),
    };
  }, [meta]);

  const handleQuestionFilterChange = useCallback(
    async (filterQueryFromCommonFilter) => {
      const nextQuery = buildQuestionFilterQuery(
        {
          search: filterQueryFromCommonFilter.search_term || "",
          startDate: filterQueryFromCommonFilter.selectedStartDate || "",
          endDate: filterQueryFromCommonFilter.selectedEndDate || "",

          grade: filterQueryFromCommonFilter.selectedGrades || [],
          language: filterQueryFromCommonFilter.selectedLanguages || [],
          subject: filterQueryFromCommonFilter.selectedsubjects || [],
          term: filterQueryFromCommonFilter.selectedTerms || [],
          author: filterQueryFromCommonFilter.selectedAuthors || [],
          chapter: filterQueryFromCommonFilter.selectedChapters || [],
          difficulty: filterQueryFromCommonFilter.selectedDifficulties || [],
          category: filterQueryFromCommonFilter.selectedCategories || [],
          type: filterQueryFromCommonFilter.selectedTypes || [],
          source: filterQueryFromCommonFilter.selectedSources || [],

          // Common Filter supports both selectedCreators and selectedTeachers.
          // Question Library backend expects selectedTeachers.
          createdBy:
            filterQueryFromCommonFilter.selectedTeachers ||
            filterQueryFromCommonFilter.selectedCreators ||
            [],

          year: filterQueryFromCommonFilter.selectedYears || [],

          sort:
            filterQueryFromCommonFilter.sort_column === "created_at" &&
            filterQueryFromCommonFilter.sort_direction === "ASC"
              ? "created-asc"
              : filterQueryFromCommonFilter.sort_column === "title"
              ? "title"
              : "created-desc",
        },
        query
      );

      await applyQuery(nextQuery);
    },
    [applyQuery, query]
  );

  return (
    <Filter
      metaDataOptions={metaDataOptions}
      onQueryChange={handleQuestionFilterChange}
      apiFilters={query}
      // syncKey={activeTab}
      syncKey="question-library"
      count={count}
      countLabel={countLabel}
      searchAriaLabel={searchAriaLabel}
      panelFirst={panelFirst}
      filterConfigOverride={questionLibraryFilterConfig}
      extraInitialFilters={{
        category: [],
        type: [],
      }}
    />
  );
}
